import {
  type GeometryResolver,
  type HighlightsRenderer,
  type HighlightsRenderingStrategy
} from '@databases/highlights';
import {
  DomHighlightsRenderer,
  ProjectedHighlightsRenderer
} from '@databases/highlights/internal';

/**
 * Projects shared highlights into Timeline's fixed and scrolling DOM panes so
 * the browser moves both fragments with their content.
 */
export function createTimelineHighlightsRenderingStrategy({
  scrollingContainer,
  scrollingResolver,
  pinnedContainer,
  pinnedResolver,
  getPinnedColumnCount
}: {
  scrollingContainer: HTMLElement;
  scrollingResolver: GeometryResolver;
  pinnedContainer: HTMLElement;
  pinnedResolver: GeometryResolver;
  getPinnedColumnCount: () => number;
}): HighlightsRenderingStrategy {
  const renderers = new Set<ProjectedHighlightsRenderer>();

  return {
    createLayer() {
      const renderer = new ProjectedHighlightsRenderer({
        baseRenderer: new DomHighlightsRenderer(scrollingContainer, scrollingResolver),
        getPinnedColumnCount,
        getBaseFragmentZIndex: ({ zIndex }) => zIndex,
        projectRowsAndContainerToPinnedSurface: true,
        acquirePinnedLayer: () => {
          const pinnedRenderer = new DomHighlightsRenderer(
            pinnedContainer,
            pinnedResolver
          );

          return {
            renderer: pinnedRenderer,
            release: () => pinnedRenderer.dispose()
          };
        },
        onDispose: () => renderers.delete(renderer)
      });

      renderers.add(renderer);

      const layer: HighlightsRenderer = {
        add: (options) => renderer.add(options),
        update: (range, mode) => renderer.update(range, mode),
        hideAll: () => renderer.hideAll(),
        showAll: () => renderer.showAll(),
        redrawAll: () => renderer.reprojectAll(),
        remove: (id) => renderer.remove(id),
        clear: () => renderer.clear(),
        dispose: () => renderer.dispose()
      };

      return layer;
    },

    dispose() {
      for (const renderer of [...renderers]) {
        renderer.dispose();
      }

      renderers.clear();
    }
  };
}
